import React from 'react';
import { get } from 'lodash';
import { withRouter } from 'react-router-dom';

// local dependencies
import { memberDetailsSavingsWidget as MemberDetailsSavingsWidgetView } from 'ui/views/widgets/memberDetailsSavingsWidget';
import { MemberDetailsSavingsWidgetService } from 'services/memberDetailsSavingsWidget';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsSavingsWidget } from 'ui/containers/MemberDetailsSavingsWidget';
 *
 * // render
 * <MemberDetailsSavingsWidget propName='propValue'/>
 */

class _MemberDetailsSavingsWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.handleActionClick = this.handleActionClick.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: props,
        };

        // create service instance
        this.memberDetailsSavingsWidgetService = new MemberDetailsSavingsWidgetService();
        
        // start component data fetch
        this.fetchComponentData();
    }

    // /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'MemberDetailsSavingsWidget.fetchComponentData()' );

        try {
            const { status, data }  = await this.memberDetailsSavingsWidgetService.fetchComponentData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
                memberId: this.props.memberId,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    async displayAlternateDrugs( { url } ) {
        log.debug( 'MemberDetailsSavingsWidget.displayAlternateDrugs()' );

        // cancel ongoing request
        this.memberDetailsSavingsWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailsSavingsWidgetService.fetchAlternateDrugsData( {
            url: url,
            data: this.props.requestPayload,
            memberId: this.props.memberId,
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.ALTERNATE_DRUGS,
                    showCloseButton: false,
                    ignoreOutsideClick: true,
                },
                config: {},
                data: {
                    ...data,
                    requestPayload: this.props.requestPayload,

                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        }  else {
            errorOverlay.open(); // show error
        }

    }

    handleActionClick( { value } ) {
        log.debug( 'MemberDetailsSavingsWidget.handleActionClick()' );

        if( 'savingsUpTo' === value.name ) {

            this.displayAlternateDrugs( {
                url: value.url
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {

        this.memberDetailsSavingsWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (

            <MemberDetailsSavingsWidgetView
                isFetching={ this.state.isFetching }
                { ...get( this.state, 'componentData', {} ) }
                networkStatus={ this.state.networkStatus }
                className={ this.props.className }
                onActionClick={ this.handleActionClick }
            />

        );
    }
}

// set display name

_MemberDetailsSavingsWidget.displayName = 'MemberDetailsSavingsWidget';

// set default props
_MemberDetailsSavingsWidget.defaultProps = {
    memberId: null,
    requestPayload: {},
};


export const MemberDetailsSavingsWidget = withRouter( _MemberDetailsSavingsWidget );
